import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import HeroVideo1 from '../../assets/video/HeroVideo.mp4';
import HeroVideo2 from '../../assets/video/herovideo4.mp4';
import HeroVideo3 from '../../assets/video/herovideo3.mp4';
import HeroVideo4 from '../../assets/video/herovideo2.mp4';

const getSlides = (t) => [
  { video: HeroVideo1, eyebrow: t('hero.slide1.eyebrow', 'Delvante Global — Immigration Advisory'), title: <>{t('hero.slide1.title1', 'Your')} <em>{t('hero.slide1.em', 'European Life')}</em><br />{t('hero.slide1.title2', 'Starts Here')}</>, subtitle: t('hero.slide1.subtitle', 'Expert visa guidance for individuals and families ready to work remotely, retire, or invest across Europe. Transparent, personal, trusted.'), primaryLabel: t('hero.slide1.cta', 'Start Your Visa Assessment'), primaryAction: 'quiz', secondaryLabel: t('hero.slide1.link', 'Explore Pathways →'), secondaryAction: '#intent' },
  { video: HeroVideo2, eyebrow: t('hero.slide2.eyebrow', 'Digital Nomad Visas'), title: <>{t('hero.slide2.title1', 'Work from')} <em>{t('hero.slide2.em', 'Anywhere')}</em><br />{t('hero.slide2.title2', 'in Europe')}</>, subtitle: t('hero.slide2.subtitle', 'Italy, Greece, Portugal, Spain — live where you\'ve always dreamed while your career continues remotely.'), primaryLabel: t('hero.slide2.cta', 'Find My Visa Pathway'), primaryAction: 'quiz', secondaryLabel: t('hero.slide2.link', 'Explore Destinations →'), secondaryAction: '#countries' },
  { video: HeroVideo3, eyebrow: t('hero.slide3.eyebrow', 'Golden Visa & Investment'), title: <>{t('hero.slide3.title1', 'Invest in')} <em>{t('hero.slide3.em', 'Europe,')}</em><br />{t('hero.slide3.title2', 'Invest in Yourself')}</>, subtitle: t('hero.slide3.subtitle', 'Residency through qualifying investment in Italy, Greece, Portugal, France, and Malta.'), primaryLabel: t('hero.slide3.cta', 'Book a Consultation'), primaryAction: 'quiz', secondaryLabel: t('hero.slide3.link', 'View Pathways →'), secondaryAction: '#intent' },
  { video: HeroVideo4, eyebrow: t('hero.slide4.eyebrow', 'Retirement & Family Visas'), title: <>{t('hero.slide4.title1', 'Retire')} <em>{t('hero.slide4.em', 'Beautifully,')}</em><br />{t('hero.slide4.title2', 'Live Fully')}</>, subtitle: t('hero.slide4.subtitle', 'Elective Residency and retirement visa programmes across six stunning EU destinations.'), primaryLabel: t('hero.slide4.cta', 'Start Free Assessment'), primaryAction: 'quiz', secondaryLabel: t('hero.slide4.link', 'Choose a Country →'), secondaryAction: '#countries' },
];

const HeroSection = ({ onOpenQuiz, t }) => {
  const { addLanguagePrefix } = useLanguage();
  const defaultT = (key, fb) => fb;
  const tt = t || defaultT;
  const HERO_SLIDES = getSlides(tt);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState(new Set([0])); // track which slides are loaded
  const timerRef = useRef(null);
  const videoRefs = useRef([]);
  const totalSlides = HERO_SLIDES.length;

  // ── Manage video playback: only active slide plays ──
  useEffect(() => {
    videoRefs.current.forEach((videoEl, index) => {
      if (!videoEl) return;
      if (index === currentSlide) {
        // Play active video
        videoEl.play().catch(() => {});
      } else {
        // Pause inactive videos to free resources
        videoEl.pause();
        // Reset to start so it's ready for next time
        if (Math.abs(index - currentSlide) > 1) {
          videoEl.currentTime = 0;
        }
      }
    });

    // Preload the next slide
    const nextIdx = (currentSlide + 1) % totalSlides;
    if (!loadedSlides.has(nextIdx)) {
      setLoadedSlides(prev => new Set([...prev, nextIdx]));
    }
  }, [currentSlide, totalSlides, loadedSlides]);

  // ── Slide timer ──
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % totalSlides);
    }, 6000);
  }, [totalSlides]);

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [resetTimer]);

  const goToSlide = (n) => {
    setCurrentSlide(n);
    // Mark this slide as loaded
    if (!loadedSlides.has(n)) {
      setLoadedSlides(prev => new Set([...prev, n]));
    }
    resetTimer();
  };

  const handlePrimaryClick = (e, action) => {
    e.preventDefault();
    if (action === 'quiz' && onOpenQuiz) onOpenQuiz();
  };

  const handleSecondaryClick = (e, target) => {
    e.preventDefault();
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Pause all videos when tab is hidden ──
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        videoRefs.current.forEach(v => v?.pause());
      } else {
        const active = videoRefs.current[currentSlide];
        if (active) active.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [currentSlide]);

  return (
    <section className="dv-hero" id="hero">
      {/* Background video slides */}
      {HERO_SLIDES.map((slide, index) => {
        const isActive = index === currentSlide;
        const isLoaded = loadedSlides.has(index);

        return (
          <div
            key={`slide-${index}`}
            className={`dv-hero-slide ${isActive ? 'active' : ''}`}
            id={`slide-${index}`}
          >
            <div className="dv-hero-slide-bg">
              {(isLoaded || isActive) ? (
                <video
                  ref={el => { videoRefs.current[index] = el; }}
                  className="dv-hero-video"
                  src={slide.video}
                  autoPlay={isActive}
                  muted
                  loop
                  playsInline
                  preload={index === 0 ? 'auto' : 'metadata'}
                  // iOS compatibility
                  disableRemotePlayback
                  // Prevent download while paused
                  controlsList="nodownload nofullscreen"
                />
              ) : (
                /* Placeholder for unloaded slides — dark background */
                <div className="dv-hero-video" style={{ background: '#0a121e' }} />
              )}
            </div>
          </div>
        );
      })}

      {/* Single permanent overlay */}
      <div className="dv-hero-overlay" />

      {/* Text layers */}
      {HERO_SLIDES.map((slide, index) => (
        <div
          key={`text-${index}`}
          className={`dv-hero-slide-text ${index === currentSlide ? 'active' : ''}`}
          id={`text-${index}`}
        >
          <p className="dv-hero-eyebrow">{slide.eyebrow}</p>
          <h1>{slide.title}</h1>
          <p className="dv-hero-sub">{slide.subtitle}</p>
          <div className="dv-hero-actions">
            <a href="#" className="dv-btn-primary" onClick={(e) => handlePrimaryClick(e, slide.primaryAction)}>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {slide.primaryLabel}
            </a>
            <a href={slide.secondaryAction} className="dv-btn-outline" onClick={(e) => handleSecondaryClick(e, slide.secondaryAction)}>
              {slide.secondaryLabel}
            </a>
          </div>
        </div>
      ))}

      {/* Slide indicators */}
      <div className="dv-hero-indicators">
        {HERO_SLIDES.map((_, index) => (
          <div
            key={`dot-${index}`}
            className={`dv-hero-dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>

      {/* Platform Badge */}
      <Link to={addLanguagePrefix('/login')} className="dv-platform-badge">
        <div className="dv-platform-badge-icon">
          <svg viewBox="0 0 24 24">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
          </svg>
        </div>
        <div className="dv-platform-badge-text">
          <span className="dv-platform-badge-label">{tt('platform.badgeLabel', 'Exclusive Access')}</span>
          <span className="dv-platform-badge-title">{tt('platform.badgeTitle', 'Members Platform →')}</span>
        </div>
      </Link>

      {/* Scroll indicator */}
      <div className="dv-hero-scroll">
        <span>{tt('hero.scroll', 'Scroll')}</span>
        <div className="dv-scroll-line" />
      </div>
    </section>
  );
};

export default HeroSection;
